import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, doc, getDoc, updateDoc, serverTimestamp, query, orderBy, limit, startAfter, where } from 'firebase/firestore';
import { db } from '../config/firebase';
import { getFamilyCompletionData } from '../utils/familyCompletion';
import { getBadgeName } from '../utils/badgeSystem';
import { formatPassageLabel } from '../utils/passagePlans';

function Dashboard() {
  const { currentUser, loading: authLoading } = useAuth();
  
  console.log('🏠 Dashboard render - currentUser:', currentUser, 'authLoading:', authLoading);
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [currentRecordingPlan, setCurrentRecordingPlan] = useState(null);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [planChapters, setPlanChapters] = useState([]);
  const [nextChapters, setNextChapters] = useState([]);
  const [currentPassagePlanSub, setCurrentPassagePlanSub] = useState(null);
  const [passagePlanProgress, setPassagePlanProgress] = useState(null);
  const [passagePlanPassages, setPassagePlanPassages] = useState([]);
  const [nextPassage, setNextPassage] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [familyPhotoUrl, setFamilyPhotoUrl] = useState('');
  const [userBadges, setUserBadges] = useState([]);
  const [familyCompletionData, setFamilyCompletionData] = useState(null);
  const [favoritedVerses, setFavoritedVerses] = useState([]);
  const [favoritedVersesLoading, setFavoritedVersesLoading] = useState(false);
  const [favoritedVersesPage, setFavoritedVersesPage] = useState(1);
  const [favoritedVersesTotal, setFavoritedVersesTotal] = useState(0);
  const [favoritedVersesHasMore, setFavoritedVersesHasMore] = useState(false);
  const [recentActivity, setRecentActivity] = useState([]);

  // Tutorial invite card — dismissed permanently once closed; the Tutorial
  // itself stays reachable from Profile > More at any time.
  const [tutorialCardDismissed, setTutorialCardDismissed] = useState(true);
  const dismissTutorialCard = async () => {
    setTutorialCardDismissed(true);
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), { tutorialCardDismissed: true });
    } catch (e) {
      console.error('Error dismissing tutorial card:', e);
    }
  };

  // Plan completion state
  const [planCompletionStatus, setPlanCompletionStatus] = useState(null);
  const [showPlanCompletionModal, setShowPlanCompletionModal] = useState(false);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [completedPlans, setCompletedPlans] = useState([]);

  useEffect(() => {
    console.log('🏠 Dashboard useEffect - currentUser:', currentUser, 'authLoading:', authLoading);
    
    // Wait for auth to finish loading AND currentUser to be available
    if (authLoading === true) {
      console.log('⏳ Auth still loading, waiting...');
      return;
    }
    
    // If auth is done loading but no currentUser, wait a bit more
    if (authLoading === false && !currentUser) {
      console.log('⏳ Auth done but no currentUser yet, waiting...');
      return;
    }
    
    if (!currentUser) {
      console.log('❌ No currentUser after auth loaded, redirecting to auth...');
      navigate('/auth');
      return;
    }

    console.log('✅ User authenticated, checking onboarding...');
    // AuthContext already fetched this same users/{uid} doc and merged it onto
    // currentUser (familyIds, signupIntent, etc.) — re-fetching it here was a
    // second, unguarded network call with no timeout on the gating path: if it
    // ever hung on a slow/cold mobile connection, fetchDashboardData() (whose
    // try/finally is what flips isLoading to false) never got called at all,
    // leaving the skeletons spinning until the page was remounted. Read what's
    // already in memory instead of the network.
    const hasFamilies = currentUser.familyIds && currentUser.familyIds.length > 0;
    if (hasFamilies) {
      fetchDashboardData();
    } else {
      // No family yet — send them back to finish setup
      navigate(`/onboard?intent=${currentUser.signupIntent || 'create'}`);
    }
  }, [currentUser, authLoading, navigate]);


  // Refresh data when page becomes visible (user returns from recording)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && currentUser) {
        console.log('🔄 Dashboard: Page became visible, refreshing data...');
        // Add a small delay to ensure any pending Firestore updates are complete
        setTimeout(() => {
          fetchDashboardData();
        }, 500);
      }
    };

    // Also refresh when the page gains focus (more reliable than visibility change)
    const handleFocus = () => {
      if (currentUser) {
        console.log('🔄 Dashboard: Page gained focus, refreshing data...');
        // Add a small delay to ensure any pending Firestore updates are complete
        setTimeout(() => {
          fetchDashboardData();
        }, 500);
      }
    };

    // Listen for recordings uploaded event from Record page
    const handleRecordingsUploaded = (event) => {
      if (currentUser) {
        console.log('🔄 Dashboard: Recordings uploaded event received, refreshing data...', event.detail);
        // Add a small delay to ensure Firestore updates are complete
        setTimeout(() => {
          fetchDashboardData();
        }, 1000);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('recordingsUploaded', handleRecordingsUploaded);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('recordingsUploaded', handleRecordingsUploaded);
    };
  }, [currentUser]);

  // Fetch plan data including available plans and completed plans.
  // freshData param: on a fresh login the `userData` state hasn't rendered yet
  // when this runs mid-fetch — passing the just-fetched doc avoids the early
  // return that used to leave the plan sections skeletal until a refocus.
  const fetchPlanData = async (freshData = null) => {
    const user = freshData || userData;
    if (!currentUser || !user) return;

    try {
      // Fetch all available plans
      const plansSnapshot = await getDocs(collection(db, 'plans'));
      const allPlans = plansSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setAvailablePlans(allPlans);

      // Fetch family's completed plans (all families in parallel)
      let familyCompletedPlans = [];
      if (user?.familyIds && user.familyIds.length > 0) {
        const perFamily = await Promise.all(user.familyIds.map(async (familyId) => {
          const familyCompletedPlansSnapshot = await getDocs(
            collection(db, `families/${familyId}/completedPlans`)
          );
          return familyCompletedPlansSnapshot.docs.map(doc => ({
            id: doc.id,
            familyId: familyId,
            ...doc.data()
          }));
        }));
        // A user in multiple families that completed the same plan would
        // otherwise show that plan once per family — dedupe by planId so the
        // Listening Plans list shows each plan once.
        const seenPlanIds = new Set();
        familyCompletedPlans = perFamily.flat().filter(plan => {
          if (seenPlanIds.has(plan.planId)) return false;
          seenPlanIds.add(plan.planId);
          return true;
        });
      }

      setCompletedPlans(familyCompletedPlans);

      // Check for recently completed plans that need acknowledgment
      const recentCompletedPlans = familyCompletedPlans.filter(plan => {
        const completedAt = plan.completedAt?.toDate?.() || new Date(plan.completedAt);
        const oneDayAgo = new Date();
        oneDayAgo.setDate(oneDayAgo.getDate() - 1);
        return completedAt > oneDayAgo;
      });

      if (recentCompletedPlans.length > 0) {
        const latestCompletedPlan = recentCompletedPlans[0];
        const planData = allPlans.find(plan => plan.id === latestCompletedPlan.planId);
        if (planData) {
          setPlanCompletionStatus({
            planId: latestCompletedPlan.planId,
            planName: planData.name,
            completedAt: latestCompletedPlan.completedAt
          });
          setShowPlanCompletionModal(true);
        }
      }

    } catch (error) {
      console.error('Error fetching plan data:', error);
    }
  };

  // Handle plan completion acknowledgment
  const handlePlanCompletionAcknowledgment = async () => {
    setShowPlanCompletionModal(false);
    setPlanCompletionStatus(null);
    // Refresh dashboard data to show new plan selection options
    await fetchDashboardData();
  };

  const fetchDashboardData = async () => {
    if (!currentUser) return;

    // ── Stale-while-revalidate ────────────────────────────────────────────────
    // Load cached stable data (profile, photo, badges, plan) and show instantly.
    // Dynamic data (recent recordings, completion) always fetches fresh below.
    const cacheKey = `db_v1_${currentUser.uid}`;
    let hasCachedData = false;
    try {
      const cached = JSON.parse(localStorage.getItem(cacheKey) || 'null');
      if (cached) {
        if (cached.userData) {
          setUserData(cached.userData);
          setTutorialCardDismissed(!!cached.userData.tutorialCardDismissed);
        }
        if (cached.familyPhotoUrl) setFamilyPhotoUrl(cached.familyPhotoUrl);
        if (cached.userBadges) setUserBadges(cached.userBadges);
        if (cached.currentRecordingPlan) setCurrentRecordingPlan(cached.currentRecordingPlan);
        if (cached.currentPlan) setCurrentPlan(cached.currentPlan);
        if (cached.planChapters) setPlanChapters(cached.planChapters);
        if (cached.nextChapters) setNextChapters(cached.nextChapters);
        if (cached.currentPassagePlanSub) setCurrentPassagePlanSub(cached.currentPassagePlanSub);
        if (cached.passagePlanProgress) setPassagePlanProgress(cached.passagePlanProgress);
        if (cached.passagePlanPassages) setPassagePlanPassages(cached.passagePlanPassages);
        if (cached.nextPassage) setNextPassage(cached.nextPassage);
        setIsLoading(false);
        hasCachedData = true;
      }
    } catch (e) {}
    if (!hasCachedData) setIsLoading(true);
    // ─────────────────────────────────────────────────────────────────────────

    const toCache = {};
    let freshCompletion = null;

    try {
      const t0 = performance.now();
      const mark = (label) => console.log(`⏱️ Dashboard load: ${label} done at ${Math.round(performance.now() - t0)}ms`);

      // Fetch user data first — everything else depends on familyIds
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      const freshUserData = userDoc.exists() ? userDoc.data() : null;
      if (freshUserData) {
        setUserData(freshUserData);
        setTutorialCardDismissed(!!freshUserData.tutorialCardDismissed);
        setUserBadges(freshUserData.badges || []);
        toCache.userData = freshUserData;
        toCache.userBadges = freshUserData.badges || [];
      }
      mark('user doc');

      const familyIds = freshUserData?.familyIds || [];

      // The branches below are independent — run them in parallel. They used
      // to run strictly one after another, which on mobile network latency
      // added up to a 20s+ skeleton screen on a fresh (uncached) login.

      // Branch: family photo + completion, then the recording-plan chain
      // (kept in one lane — the next-chapter calc needs freshCompletion).
      const planChainBranch = (async () => {
        if (freshUserData && familyIds.length > 0) {
          try {
            const familyDoc = await getDoc(doc(db, 'families', familyIds[0]));
            if (familyDoc.exists()) {
              const photoUrl = familyDoc.data().familyPhotoUrl || '';
              setFamilyPhotoUrl(photoUrl);
              toCache.familyPhotoUrl = photoUrl;
            }

            const perFamily = await Promise.all(
              familyIds.map(fid => getFamilyCompletionData(fid, db))
            );
            const chapterMap = new Map();
            for (const data of perFamily) {
              if (data && data.chapters) {
                for (const ch of data.chapters) {
                  const key = `${ch.book}_${ch.chapter}`;
                  if (!chapterMap.has(key) || ch.completed) chapterMap.set(key, ch);
                }
              }
            }
            freshCompletion = chapterMap.size > 0
              ? { chapters: Array.from(chapterMap.values()) }
              : null;
            setFamilyCompletionData(freshCompletion);
          } catch (error) {
            console.error('Error fetching family data:', error);
          }
          mark('family completion');
        }

        // Fetch user's recording plans — use the active plan, not docs[0]
        const recordingPlansSnapshot = await getDocs(
          collection(db, `users/${currentUser.uid}/recordingPlans`)
        );

        const activePlanDoc = recordingPlansSnapshot.docs.find(d => d.data().status === 'active');
        if (activePlanDoc) {
          const recordingPlan = activePlanDoc.data();
          setCurrentRecordingPlan(recordingPlan);
          toCache.currentRecordingPlan = recordingPlan;

          // Fetch the actual plan data
          const planDoc = await getDoc(doc(db, 'plans', recordingPlan.planId));
          if (planDoc.exists()) {
            const planData = planDoc.data();
            setCurrentPlan(planData);
            setPlanChapters(planData.chapters || []);
            toCache.currentPlan = planData;
            toCache.planChapters = planData.chapters || [];

            // Calculate next chapters using freshCompletion (local var, not stale state)
            const allChapters = planData.chapters || [];
            const nextChaptersToRecord = allChapters.filter(chapter => {
              const chapterKey = `${chapter.book}_${chapter.chapter}`;
              const chapterData = freshCompletion?.chapters?.find(
                ch => ch.book === chapter.book && ch.chapter === chapter.chapter
              );
              const isFamilyChapterComplete = chapterData?.completed || false;
              const isIndividualComplete = recordingPlan.chapterProgress?.[chapterKey]?.isComplete;
              const isInCompletedChapters = (recordingPlan.completedChapters || []).includes(chapterKey);
              return !(isFamilyChapterComplete || isIndividualComplete || isInCompletedChapters);
            });

            const nextThree = nextChaptersToRecord.slice(0, 3);
            setNextChapters(nextThree);
            toCache.nextChapters = nextThree;
          }
        } else {
          setCurrentRecordingPlan(null);
          setPlanChapters([]);
          setNextChapters([]);
          toCache.currentRecordingPlan = null;
          toCache.currentPlan = null;
          toCache.planChapters = [];
          toCache.nextChapters = [];
        }
        mark('recording plan');
      })();

      // Branch: available + completed plans
      const planDataBranch = fetchPlanData(freshUserData).then(() => mark('plan data'));

      // Branch: active Passages Plan subscription — family-wide progress,
      // read from the passagePlanProgress doc that Record.js keeps updated
      // after every upload (see usePassagePlanManagement.updatePassagePlanProgress).
      const passagePlanBranch = (async () => {
        try {
          const subsSnap = await getDocs(
            collection(db, `users/${currentUser.uid}/passagePlanSubscriptions`)
          );
          const lastPlanId = freshUserData?.lastPassagePlanId;
          const activeSub = lastPlanId
            ? subsSnap.docs.find(d => d.id === lastPlanId)
            : subsSnap.docs[0];
          if (!activeSub) {
            setCurrentPassagePlanSub(null);
            setPassagePlanProgress(null);
            setPassagePlanPassages([]);
            setNextPassage(null);
            toCache.currentPassagePlanSub = null;
            toCache.passagePlanProgress = null;
            toCache.passagePlanPassages = [];
            toCache.nextPassage = null;
            mark('passage plan');
            return;
          }

          const subData = activeSub.data();
          const planDoc = await getDoc(doc(db, 'passagePlans', subData.planId));
          // Use the live plan name so renames are reflected without resubscribing.
          const liveSub = planDoc.exists()
            ? { ...subData, planName: planDoc.data().name || subData.planName }
            : subData;
          setCurrentPassagePlanSub(liveSub);
          toCache.currentPassagePlanSub = liveSub;

          const passages = planDoc.exists() ? (planDoc.data().passages || []) : [];
          setPassagePlanPassages(passages);
          toCache.passagePlanPassages = passages;

          let progress = null;
          const progressDoc = await getDoc(
            doc(db, `users/${currentUser.uid}/passagePlanProgress`, subData.planId)
          );
          if (progressDoc.exists()) progress = progressDoc.data();
          setPassagePlanProgress(progress);
          toCache.passagePlanProgress = progress;

          const doneSet = new Set(progress?.completedPassageIndexes || []);
          const next = passages.find((_, i) => !doneSet.has(i)) || null;
          setNextPassage(next);
          toCache.nextPassage = next;
        } catch (error) {
          console.error('Error fetching passage plan data:', error);
        }
        mark('passage plan');
      })();

      // Branch: recent family activity — always fresh, never cached
      const activityBranch = (async () => {
        try {
          if (familyIds.length > 0) {
            const memberIds = new Set([currentUser.uid]);
            const famDocs = await Promise.all(
              familyIds.map(fid => getDoc(doc(db, 'families', fid)))
            );
            for (const famDoc of famDocs) {
              if (famDoc.exists()) {
                const fd = famDoc.data();
                (fd.memberIDs || fd.memberIds || []).forEach(id => memberIds.add(id));
              }
            }
            const ids = [...memberIds];
            const batchQueries = [];
            for (let i = 0; i < ids.length; i += 10) {
              batchQueries.push(getDocs(query(
                collection(db, 'recordings'),
                where('userId', 'in', ids.slice(i, i + 10)),
                limit(50)
              )));
            }
            const snaps = await Promise.all(batchQueries);
            const allRec = [];
            snaps.forEach(snap => snap.docs.forEach(d => allRec.push({ id: d.id, ...d.data() })));
            const getMs = v => v?.toDate ? v.toDate().getTime() : v ? new Date(v).getTime() : 0;
            allRec.sort((a, b) => getMs(b.createdAt) - getMs(a.createdAt));
            setRecentActivity(allRec.slice(0, 5));
          }
        } catch (e) {
          console.error('Error fetching recent activity:', e);
        }
        mark('recent activity');
      })();

      // Branch: favorited verses — always fresh
      const favoritesBranch = fetchFavoritedVerses(1, true).then(() => mark('favorites'));

      await Promise.all([planChainBranch, planDataBranch, activityBranch, favoritesBranch, passagePlanBranch]);
      mark('TOTAL');

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setIsLoading(false);
      // Persist stable data for next visit
      if (Object.keys(toCache).length > 0) {
        try {
          localStorage.setItem(cacheKey, JSON.stringify(toCache, (_k, v) =>
            v && typeof v.toDate === 'function' ? v.toDate().toISOString() : v
          ));
        } catch (e) {}
      }
    }
  };

  const handleRecordNext = () => {
    if (nextChapters.length > 0) {
      const nextChapter = nextChapters[0];
      // Navigate to Record page with pre-populated chapter
      navigate(`/record?book=${nextChapter.book}&chapter=${nextChapter.chapter}`);
    } else {
      // Navigate to Record page without pre-population
      navigate('/record');
    }
  };

  const handleRecordPassagePlanNext = () => {
    if (nextPassage) {
      navigate(`/record?book=${nextPassage.book}&chapter=${nextPassage.chapter}&start=${nextPassage.startVerse}&end=${nextPassage.endVerse}`);
    } else {
      navigate('/record');
    }
  };

  const handleChapterClick = (chapter) => {
    const chapterKey = `${chapter.book}_${chapter.chapter}`;
    const chapterData = familyCompletionData?.chapters?.find(ch =>
      ch.book === chapter.book && ch.chapter === chapter.chapter
    );
    const isComplete =
      chapterData?.completed ||
      currentRecordingPlan?.chapterProgress?.[chapterKey]?.isComplete ||
      (currentRecordingPlan?.completedChapters || []).includes(chapterKey);

    if (isComplete) {
      navigate(`/listen?book=${chapter.book}&chapter=${chapter.chapter}`);
    } else {
      navigate(`/record?book=${chapter.book}&chapter=${chapter.chapter}`);
    }
  };

  const getUserFirstName = () => {
    if (!userData) return 'Friend';
    
    if (userData.firstName) {
      return userData.firstName;
    }
    
    if (userData.displayName) {
      const nameParts = userData.displayName.split(' ');
      return nameParts[0];
    }
    
    return 'Friend';
  };

  // Chapters in the current plan that THIS USER personally recorded.
  const getMyRecordedPlanChapters = () => {
    if (!planChapters.length || !currentRecordingPlan) return [];
    const planChapterKeys = new Set(planChapters.map(ch => `${ch.book}_${ch.chapter}`));
    return planChapters.filter(ch => {
      const key = `${ch.book}_${ch.chapter}`;
      if (!planChapterKeys.has(key)) return false;
      return (currentRecordingPlan.completedChapters || []).includes(key) ||
        !!currentRecordingPlan.chapterProgress?.[key]?.isComplete;
    });
  };

  // Helper function to get only completed chapters that are part of the current plan.
  // Checks both family completion data AND the plan's own completedChapters list so that
  // either source of truth is sufficient to show a chapter as done.
  const getCompletedPlanChapters = () => {
    if (!planChapters.length) return [];

    const planChapterKeys = new Set(planChapters.map(ch => `${ch.book}_${ch.chapter}`));
    const completedKeys = new Set();

    // Source 1: family-wide completion document
    if (familyCompletionData?.chapters) {
      familyCompletionData.chapters
        .filter(ch => ch.completed && planChapterKeys.has(`${ch.book}_${ch.chapter}`))
        .forEach(ch => completedKeys.add(`${ch.book}_${ch.chapter}`));
    }

    // Source 2: plan subscription doc's own completedChapters (kept current by updatePlanProgress)
    (currentRecordingPlan?.completedChapters || [])
      .filter(key => planChapterKeys.has(key))
      .forEach(key => completedKeys.add(key));

    return planChapters.filter(ch => completedKeys.has(`${ch.book}_${ch.chapter}`));
  };

  const getMostRecentBadge = () => {
    if (!userBadges || userBadges.length === 0) return null;
    
    // Sort badges by earnedAt date (most recent first)
    const sortedBadges = userBadges
      .filter(badge => badge.earnedAt)
      .sort((a, b) => new Date(b.earnedAt) - new Date(a.earnedAt));
    
    return sortedBadges.length > 0 ? sortedBadges[0] : null;
  };

  const shouldShowBadge = () => {
    const mostRecentBadge = getMostRecentBadge();
    if (!mostRecentBadge || !mostRecentBadge.earnedAt) return false;
    
    // Check if badge was earned within the last week (7 days)
    const badgeEarnedDate = new Date(mostRecentBadge.earnedAt);
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
    
    return badgeEarnedDate > oneWeekAgo;
  };


  // Function to fetch favorited verses with pagination
  const fetchFavoritedVerses = async (page = 1, reset = false) => {
    if (!currentUser) return;
    
    setFavoritedVersesLoading(true);
    try {
      const versesPerPage = 5;
      const startIndex = (page - 1) * versesPerPage;
      
      // Query favorited verses ordered by favoritedAt (most recent first)
      const favoritedVersesQuery = query(
        collection(db, `users/${currentUser.uid}/favoritedVerses`),
        orderBy('favoritedAt', 'desc'),
        limit(versesPerPage)
      );
      
      const snapshot = await getDocs(favoritedVersesQuery);
      const verses = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      if (reset) {
        setFavoritedVerses(verses);
        setFavoritedVersesPage(1);
      } else {
        setFavoritedVerses(prev => [...prev, ...verses]);
        setFavoritedVersesPage(page);
      }
      
      // Check if there are more verses
      setFavoritedVersesHasMore(verses.length === versesPerPage);
      
      // Get total count for pagination info
      const totalSnapshot = await getDocs(collection(db, `users/${currentUser.uid}/favoritedVerses`));
      setFavoritedVersesTotal(totalSnapshot.docs.length);
      
    } catch (error) {
      console.error('Error fetching favorited verses:', error);
    } finally {
      setFavoritedVersesLoading(false);
    }
  };

  // Load more favorited verses
  const loadMoreFavoritedVerses = () => {
    if (!favoritedVersesLoading && favoritedVersesHasMore) {
      fetchFavoritedVerses(favoritedVersesPage + 1, false);
    }
  };

  if (authLoading) return null;

  if (isLoading) {
    return (
      <div className="bg-gray-100 dark:bg-[#0d1520]" style={{ minHeight: '100vh', paddingBottom: '80px' }}>
        <div className="max-w-4xl mx-auto pt-4 p-4 pb-20 space-y-6">

          {/* Profile hero skeleton */}
          <div className="bg-white dark:bg-[#111827] rounded-[3px] shadow-sm overflow-hidden animate-pulse">
            <div className="flex items-center gap-4 p-4 pb-3">
              <div className="w-16 h-16 rounded-full bg-gray-200 dark:bg-white/10 flex-shrink-0" />
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-gray-200 dark:bg-white/10 rounded-full w-36" />
                <div className="h-3 bg-gray-100 rounded-full w-52" />
              </div>
              <div className="w-16 h-6 bg-gray-200 dark:bg-white/10 rounded-full flex-shrink-0" />
            </div>
            <div className="border-t border-gray-100 dark:border-white/10 px-4 py-3 flex gap-2">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="w-[60px] h-10 bg-gray-100 dark:bg-white/5 rounded-[3px] flex-shrink-0" />
              ))}
            </div>
          </div>

          {/* Listen card skeleton */}
          <div className="bg-white dark:bg-[#111827] rounded-[3px] shadow-sm overflow-hidden animate-pulse">
            <div className="flex flex-col sm:flex-row">
              <div className="sm:w-1/4 h-40 sm:h-auto bg-gray-200 dark:bg-white/10 flex-shrink-0" />
              <div className="flex flex-col sm:flex-row flex-1 divide-y sm:divide-y-0 sm:divide-x divide-gray-100 dark:divide-white/10">
                <div className="flex-1 p-4 space-y-3">
                  <div className="h-2.5 bg-gray-200 dark:bg-white/10 rounded-full w-28" />
                  <div className="h-20 bg-gray-100 dark:bg-white/5 rounded-[3px]" />
                </div>
                <div className="flex-1 p-4 space-y-3">
                  <div className="h-2.5 bg-gray-200 dark:bg-white/10 rounded-full w-24" />
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="h-7 bg-gray-100 dark:bg-white/5 rounded-[3px]" />
                  ))}
                </div>
                <div className="flex-1 p-4 space-y-3">
                  <div className="h-2.5 bg-gray-200 dark:bg-white/10 rounded-full w-28" />
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="h-7 bg-gray-100 dark:bg-white/5 rounded-[3px]" />
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Record card skeleton */}
          <div className="bg-white dark:bg-[#111827] rounded-[3px] shadow-sm p-4 animate-pulse space-y-3">
            <div className="flex items-center justify-between">
              <div className="h-4 bg-gray-200 dark:bg-white/10 rounded-full w-20" />
              <div className="h-3 bg-gray-100 rounded-full w-8" />
            </div>
            <div className="space-y-1.5">
              <div className="flex justify-between">
                <div className="h-3 bg-gray-200 dark:bg-white/10 rounded-full w-40" />
              </div>
              <div className="h-2 bg-gray-200 dark:bg-white/10 rounded-full w-full" />
            </div>
            <div className="flex gap-4">
              <div className="h-3 bg-gray-100 rounded-full w-24" />
              <div className="h-3 bg-gray-100 rounded-full w-28" />
            </div>
            <div className="h-10 bg-gray-200 rounded-[3px] w-full" />
          </div>

        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-100 dark:bg-[#0d1520]" style={{ minHeight: '100vh', paddingBottom: '80px' }}>
      <div className="max-w-4xl mx-auto pt-4 p-4 pb-20">

        {/* Tutorial invite card — dismissible, always reachable again from
            Profile > More > Tutorial afterward. */}
        {!tutorialCardDismissed && (
          <div className="relative mb-6 bg-gradient-to-r from-cerulean to-aqua-deep rounded-[3px] shadow-sm overflow-hidden">
            <button
              onClick={dismissTutorialCard}
              aria-label="Dismiss"
              className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <div className="p-4 pr-12 flex items-center gap-4">
              <span className="text-3xl flex-shrink-0" aria-hidden="true">👋</span>
              <div className="flex-1 min-w-0">
                <h2 className="text-white font-bold text-base leading-tight">New here? We'll show you around.</h2>
                <p className="text-white/80 text-xs mt-0.5 leading-snug">
                  A few short videos on recording, listening, and everything in between. Watch now, or come back anytime.
                </p>
              </div>
              <button
                onClick={() => navigate('/tutorial')}
                className="flex-shrink-0 bg-white text-cerulean font-semibold text-sm px-4 py-2 rounded-[3px] hover:bg-white/90 transition-colors"
              >
                Start Tutorial
              </button>
            </div>
          </div>
        )}

        {/* Profile hero card */}
        <div className="mb-6 bg-white dark:bg-[#111827] rounded-[3px] shadow-sm border border-white/20 dark:border-white/10 overflow-hidden">
          {/* Top row: photo + (name/plan line, then microtext below) */}
          <div className="flex items-center gap-3 p-4 pb-3">
            {(currentUser?.photoURL || userData?.profileImageUrl) ? (
              <img
                src={currentUser?.photoURL || userData?.profileImageUrl}
                alt="Profile"
                className="w-12 h-12 rounded-full object-cover border-2 border-cerulean/20 shadow flex-shrink-0"
              />
            ) : (
              <div className="w-12 h-12 rounded-full bg-cerulean flex items-center justify-center border-2 border-cerulean/20 shadow flex-shrink-0">
                <span className="text-white text-base font-bold">
                  {(userData?.firstName?.[0] || userData?.displayName?.[0] || currentUser?.email?.[0] || '?').toUpperCase()}
                  {(userData?.lastName?.[0] || userData?.displayName?.split(' ')?.[1]?.[0] || '').toUpperCase()}
                </span>
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <h1 className="text-lg font-bold text-space-cadet dark:text-white leading-tight truncate">
                  Welcome, {getUserFirstName()}!
                </h1>
                <button
                  onClick={() => navigate('/subscription')}
                  className="flex-shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-[3px] text-xs font-semibold bg-cerulean/10 text-cerulean dark:text-cerulean-lighter hover:bg-cerulean/20 transition-colors"
                >
                  {currentUser?.isBeta
                    ? '✦ Friends'
                    : currentUser?.subscriptionPlan === 'legacy'
                    ? 'Family Legacy'
                    : currentUser?.subscriptionPlan === 'plus'
                    ? 'Family+'
                    : 'Free Plan'}
                </button>
              </div>
              {shouldShowBadge() ? (
                <p className="text-xs text-cerulean dark:text-cerulean-lighter font-medium mt-0.5">
                  Congrats on earning the {getBadgeName(getMostRecentBadge().type)} badge!
                </p>
              ) : (
                <p className="text-xs text-gray-500 dark:text-white/40 mt-0.5 leading-snug">
                  Give your family the gift of God's Word in your own voice.
                </p>
              )}
            </div>
          </div>

          {/* Badges row */}
          <div className="border-t border-gray-100 dark:border-white/10 px-4 py-3">
            {userBadges.length > 0 ? (
              <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide pb-0.5">
                {[...userBadges]
                  .sort((a, b) => new Date(b.earnedAt?.toDate?.() || b.earnedAt) - new Date(a.earnedAt?.toDate?.() || a.earnedAt))
                  .slice(0, 8)
                  .map((badge, i) => (
                    <div
                      key={i}
                      title={getBadgeName(badge.type)}
                      className="flex-shrink-0 flex flex-col items-center gap-0.5 px-2.5 py-1.5 bg-cerulean/10 dark:bg-cerulean/15 border border-cerulean/20 dark:border-cerulean/25 rounded-[3px] text-center min-w-[60px]"
                    >
                      <span className="text-lg leading-none">
                        {badge.type.startsWith('listener') ? '🎧'
                          : badge.type.startsWith('recording-streak') ? '🔥'
                          : badge.type === 'studio-master-nt' || badge.type === 'studio-master-ot' || badge.type === 'family-founders' ? '📖'
                          : badge.type === 'studio-star' ? '⭐'
                          : badge.type === 'recording-librarian' ? '📚'
                          : badge.type === 'old-testament-warrior' ? '⚔️'
                          : badge.type === 'first-recording' ? '🎤'
                          : '🏆'}
                      </span>
                      <span className="text-[10px] font-medium text-cerulean dark:text-cerulean-lighter leading-tight text-center whitespace-nowrap">
                        {getBadgeName(badge.type)}
                      </span>
                    </div>
                  ))}
                {userBadges.length > 8 && (
                  <div className="flex-shrink-0 flex items-center justify-center w-12 h-12 rounded-[3px] bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-xs font-semibold text-gray-500 dark:text-white/50">
                    +{userBadges.length - 8}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-xs text-gray-400 dark:text-white/30 text-center py-1">
                Record your first verse to earn a badge
              </p>
            )}
          </div>
        </div>

      {/* ── Listen Card ── */}
      <div className="mb-4">
        <div className="bg-white dark:bg-[#111827] rounded-[3px] shadow-sm overflow-hidden">
          <div className="flex flex-col sm:flex-row">

            {/* Col 1 — Family photo (25%) */}
            {familyPhotoUrl && (
              <div className="sm:w-1/4 flex-shrink-0">
                <img
                  src={familyPhotoUrl}
                  alt="Family"
                  className="w-full h-40 sm:h-full object-cover object-top"
                />
              </div>
            )}

            {/* Cols 2–4 — content */}
            <div className={`flex flex-col sm:flex-row flex-1 divide-y sm:divide-y-0 sm:divide-x divide-gray-100 dark:divide-white/10`}>

              {/* Col 2 — Click to Listen */}
              <div className="flex-1 p-4 flex flex-col justify-between">
                <p className="text-xs font-semibold text-gray-400 dark:text-white/35 uppercase tracking-wider mb-2">Continue Listening</p>
                <button
                  onClick={() => navigate('/listen')}
                  className="flex-1 flex flex-col items-center justify-center gap-2 p-3 rounded-[3px] bg-cerulean/5 dark:bg-cerulean/10 hover:bg-cerulean/10 dark:hover:bg-cerulean/15 transition-colors text-center group"
                >
                  <div className="w-10 h-10 rounded-full bg-cerulean flex items-center justify-center shadow-sm group-hover:shadow-md transition-shadow">
                    <svg className="w-5 h-5 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                  </div>
                  <span className="text-sm font-semibold text-space-cadet dark:text-white">Pick up where I left off</span>
                  <span className="text-xs text-gray-400 dark:text-white/35">Opens your listening spot</span>
                </button>
              </div>

              {/* Col 3 — Listening plans (only if available) */}
              {completedPlans.length > 0 && (
                <div className="flex-1 p-4">
                  <p className="text-xs font-semibold text-gray-400 dark:text-white/35 uppercase tracking-wider mb-2">Listening Plans</p>
                  <div className="space-y-1.5">
                    {completedPlans.slice(0, 3).map(plan => (
                      <button
                        key={plan.id}
                        onClick={() => navigate('/listen')}
                        className="w-full flex items-center gap-2 p-2 rounded-[3px] hover:bg-gray-50 dark:hover:bg-white/5 transition-colors text-left"
                      >
                        <div className="w-6 h-6 rounded-full bg-green-100 dark:bg-green-400/15 flex items-center justify-center flex-shrink-0">
                          <svg className="w-3 h-3 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-space-cadet dark:text-white truncate">{plan.planName || 'Plan'}</p>
                          <p className="text-xs text-gray-400 dark:text-white/35">{plan.totalChapters} chapters</p>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Col 4 — Latest recordings */}
              <div className="flex-1 p-4">
                <p className="text-xs font-semibold text-gray-400 dark:text-white/35 uppercase tracking-wider mb-2">Latest Recordings</p>
                {recentActivity.length > 0 ? (
                  <div className="space-y-1.5">
                    {(() => {
                      const seen = new Set();
                      return recentActivity.filter(rec => {
                        const key = `${rec.book}_${rec.chapter}`;
                        if (seen.has(key)) return false;
                        seen.add(key);
                        return true;
                      }).slice(0, 3).map(rec => {
                        const bookName = rec.book ? (rec.book.charAt(0).toUpperCase() + rec.book.slice(1)) : '';
                        const who = (rec.name || rec.displayName || rec.userName || 'Family member').split(' ')[0];
                        return (
                          <button
                            key={rec.id}
                            onClick={() => navigate(`/listen?book=${rec.book}&chapter=${rec.chapter}`)}
                            className="w-full flex items-center gap-2 p-2 rounded-[3px] hover:bg-gray-50 dark:hover:bg-white/5 transition-colors text-left"
                          >
                            <div className="w-6 h-6 rounded-full bg-cerulean/10 flex items-center justify-center flex-shrink-0">
                              <svg className="w-3 h-3 text-cerulean dark:text-cerulean-lighter" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                            </div>
                            <p className="text-xs text-space-cadet dark:text-white truncate">
                              <span className="font-semibold">{who}</span>
                              <span className="text-gray-400 dark:text-white/35"> — {bookName} {rec.chapter}</span>
                            </p>
                          </button>
                        );
                      });
                    })()}
                  </div>
                ) : (
                  <p className="text-xs text-gray-400 dark:text-white/35 pt-1">No recordings yet.</p>
                )}
              </div>

            </div>
          </div>
        </div>
      </div>

      {/* ── Record Card ── */}
      <div className="mb-6">
        <div className="bg-white dark:bg-[#111827] rounded-[3px] shadow-sm p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-bold text-space-cadet dark:text-white flex items-center gap-2">
              <svg className="w-4 h-4 text-cerulean" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" /></svg>
              Record
            </h2>
          </div>

          {currentRecordingPlan ? (
            /* Has active plan — show progress + continue */
            <div>
              {(() => {
                const myCount = getMyRecordedPlanChapters().length;
                const familyCount = getCompletedPlanChapters().length;
                const total = planChapters.length;
                const isPersonal = (userData?.recordingScope || currentUser?.recordingScope) === 'personal';
                const progressCount = isPersonal ? myCount : familyCount;
                const progressPct = total > 0 ? Math.round((progressCount / total) * 100) : 0;
                return (
                  <>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-medium text-space-cadet dark:text-white">{currentPlan?.name || 'Chapter Plan'}</p>
                      <span className="text-sm font-bold text-cerulean dark:text-cerulean-lighter">{progressPct}%</span>
                    </div>
                    <div className="w-full bg-gray-100 dark:bg-white/10 rounded-full h-2 mb-3">
                      <div
                        className="bg-cerulean h-2 rounded-full transition-all duration-300"
                        style={{ width: `${progressPct}%` }}
                      />
                    </div>
                    <div className="flex gap-4 mb-3">
                      <p className="text-xs text-cerulean dark:text-cerulean-lighter font-medium">
                        {myCount} of {total} <span className="text-gray-400 dark:text-white/35 font-normal">I recorded</span>
                      </p>
                      <p className="text-xs text-gray-400 dark:text-white/35">
                        {familyCount} of {total} <span>family complete</span>
                      </p>
                    </div>
                  </>
                );
              })()}
              <div className="flex gap-2">
                <button
                  onClick={handleRecordNext}
                  className="flex-1 bg-gradient-to-r from-cerulean to-aqua-deep text-white font-semibold py-2.5 rounded-[3px] hover:opacity-90 transition-all shadow-sm text-sm"
                >
                  {nextChapters.length > 0
                    ? `Record ${nextChapters[0].book.charAt(0).toUpperCase() + nextChapters[0].book.slice(1)} ${nextChapters[0].chapter}`
                    : 'Continue Recording'}
                </button>
                <button
                  onClick={() => navigate('/plans')}
                  className="px-3 py-2.5 text-sm text-gray-400 dark:text-white/40 hover:text-cerulean dark:hover:text-cerulean-lighter border border-gray-200 dark:border-white/15 rounded-[3px] transition-colors"
                >
                  Plans
                </button>
              </div>
            </div>
          ) : (
            /* No plan — invite to choose one */
            <div className="text-center py-2">
              <p className="text-sm text-gray-500 dark:text-white/40 mb-4">Choose a guided recording plan, or record any passage freely.</p>
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  onClick={() => navigate('/plans')}
                  className="flex-1 bg-gradient-to-r from-cerulean to-aqua-deep text-white font-semibold py-2.5 rounded-[3px] hover:opacity-90 transition-all shadow-sm text-sm flex items-center justify-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" /></svg>
                  Choose a Plan
                </button>
                <button
                  onClick={() => navigate('/record')}
                  className="flex-1 border border-cerulean/30 text-cerulean dark:text-cerulean-lighter font-semibold py-2.5 rounded-[3px] hover:bg-cerulean/5 transition-all text-sm flex items-center justify-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" /></svg>
                  Record Freely
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Passages Plan Card ── */}
      <div className="mb-6">
        <div className="bg-white dark:bg-[#111827] rounded-[3px] shadow-sm p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-bold text-space-cadet dark:text-white flex items-center gap-2">
              <svg className="w-4 h-4 text-cerulean" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17H5a2 2 0 01-2-2V5a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2h-4M9 17l3 3m0 0l3-3m-3 3V9" /></svg>
              Passages
            </h2>
          </div>

          {currentPassagePlanSub ? (
            /* Has active passage plan — show progress + continue */
            <div>
              {(() => {
                const total = passagePlanPassages.length;
                const done = passagePlanProgress?.completedPassageIndexes?.length || 0;
                const pct = total > 0 ? Math.round((done / total) * 100) : 0;
                return (
                  <>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-medium text-space-cadet dark:text-white">{currentPassagePlanSub.planName || 'Passage Plan'}</p>
                      <span className="text-sm font-bold text-cerulean dark:text-cerulean-lighter">{pct}%</span>
                    </div>
                    <div className="w-full bg-gray-100 dark:bg-white/10 rounded-full h-2 mb-3">
                      <div
                        className="bg-cerulean h-2 rounded-full transition-all duration-300"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-400 dark:text-white/35 mb-3">
                      {done} of {total} <span>family complete</span>
                    </p>
                  </>
                );
              })()}
              <div className="flex gap-2">
                <button
                  onClick={handleRecordPassagePlanNext}
                  className="flex-1 bg-gradient-to-r from-cerulean to-aqua-deep text-white font-semibold py-2.5 rounded-[3px] hover:opacity-90 transition-all shadow-sm text-sm"
                >
                  {nextPassage ? `Record ${formatPassageLabel(nextPassage)}` : 'Continue Recording'}
                </button>
                <button
                  onClick={() => navigate('/passages-plans')}
                  className="px-3 py-2.5 text-sm text-gray-400 dark:text-white/40 hover:text-cerulean dark:hover:text-cerulean-lighter border border-gray-200 dark:border-white/15 rounded-[3px] transition-colors"
                >
                  Plans
                </button>
              </div>
            </div>
          ) : (
            /* No passage plan — invite to browse/subscribe */
            <div className="text-center py-2">
              <p className="text-sm text-gray-500 dark:text-white/40 mb-4">Subscribe to a curated Passage Plan — verse-by-verse sequences like the Romans Road.</p>
              <button
                onClick={() => navigate('/passages-plans')}
                className="w-full bg-gradient-to-r from-cerulean to-aqua-deep text-white font-semibold py-2.5 rounded-[3px] hover:opacity-90 transition-all shadow-sm text-sm flex items-center justify-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17H5a2 2 0 01-2-2V5a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2h-4M9 17l3 3m0 0l3-3m-3 3V9" /></svg>
                Browse Passage Plans
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Plan Completion Modal */}
      {showPlanCompletionModal && planCompletionStatus && (
        <div className="mb-8">
          <div className="bg-white dark:bg-[#111827] border border-gray-200 dark:border-white/10 shadow-sm rounded-[3px] p-6 text-center">
            <div className="w-16 h-16 bg-green-100 dark:bg-green-400/15 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-green-800 dark:text-green-400 mb-2">Plan Completed!</h2>
            <p className="text-green-700 dark:text-green-300/80 mb-4">
              Congratulations! You've completed the <strong>{planCompletionStatus.planName}</strong> plan.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={handlePlanCompletionAcknowledgment}
                className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-semibold rounded-[3px] px-6 py-3 transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                Acknowledge & Continue
              </button>
              <button
                onClick={() => navigate('/profile?openPlans=true')}
                className="border-2 border-green-300 dark:border-green-400/30 text-green-700 dark:text-green-300 hover:bg-green-100 dark:hover:bg-green-400/10 font-semibold rounded-[3px] px-6 py-3 transition-all duration-200"
              >
                Choose New Plan
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Favorited Verses Section */}
      <div className="mb-8">
        <div className="bg-white dark:bg-[#111827] rounded-[3px] shadow-lg border border-cerulean-lightest dark:border-white/10 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-space-cadet dark:text-white flex items-center">
              <svg className="w-6 h-6 text-red-500 mr-2" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
              </svg>
              Favorited Verses
              {favoritedVersesTotal > 0 && (
                <span className="ml-2 text-sm font-normal text-gray-500 dark:text-white/40">
                  ({favoritedVersesTotal} total)
                </span>
              )}
            </h2>
            {favoritedVersesTotal > 3 && (
              <button
                onClick={() => navigate('/favorites')}
                className="text-sm text-cerulean dark:text-cerulean-lighter hover:underline font-medium"
              >
                See all →
              </button>
            )}
          </div>

          {favoritedVersesLoading && favoritedVerses.length === 0 ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cerulean mx-auto mb-4"></div>
              <p className="text-gray-500 dark:text-white/40">Loading favorited verses...</p>
            </div>
          ) : favoritedVerses.length === 0 ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-gray-100 dark:bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-400 dark:text-white/30" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                </svg>
              </div>
              <p className="text-gray-500 dark:text-white/40 mb-2">No favorited verses yet</p>
              <p className="text-sm text-gray-500 dark:text-white/40">Start favoriting verses while listening to build your collection</p>
            </div>
          ) : (
            <div className="space-y-3">
              {favoritedVerses.slice(0, 3).map((verse) => (
                <div
                  key={verse.id}
                  className="flex items-start space-x-3 p-4 border border-gray-200 dark:border-white/10 rounded-[3px] hover:bg-gray-50 dark:hover:bg-white/5 transition-colors duration-200"
                >
                  <div className="flex-shrink-0 mt-1">
                    <div className="w-8 h-8 bg-red-100 dark:bg-red-400/15 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-red-500 dark:text-red-400" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                      </svg>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-semibold text-space-cadet dark:text-white truncate">
                        {verse.passage}
                      </h3>
                      <span className="text-xs text-gray-500 dark:text-white/40 flex-shrink-0 ml-2">
                        {verse.favoritedAt ? new Date(verse.favoritedAt.seconds * 1000).toLocaleDateString() : ''}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-white/60 line-clamp-2">
                      "{verse.verseText}"
                    </p>
                    <div className="mt-2">
                      <button
                        onClick={() => navigate(`/listen?book=${verse.book}&chapter=${verse.chapter}&verse=${verse.verse}`)}
                        className="text-xs text-cerulean dark:text-cerulean-lighter hover:underline transition-colors"
                      >
                        Listen to this passage →
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              {favoritedVersesTotal > 3 && (
                <div className="text-center pt-2">
                  <button
                    onClick={() => navigate('/favorites')}
                    className="px-6 py-2 bg-gray-100 dark:bg-white/10 hover:bg-gray-200 dark:hover:bg-white/15 text-gray-700 dark:text-white/70 rounded-[3px] font-medium transition-colors"
                  >
                    Show all {favoritedVersesTotal} favorites →
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      </div>
    </div>
  );
}

export default Dashboard;
